import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { api, ApiError } from '@/lib/api';
import { cn, formatDate, formatDateOnly, formatCurrency } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { PageHeader, BackLink } from '@/layouts/AppLayout';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Hotel {
  id: string;
  name: string;
  city: string;
  state: string | null;
  country: string;
  address: string | null;
  contactPerson: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  retainerStatus: 'active' | 'paused' | 'terminated';
  retainerAmount: string | null;
  defaultCommissionPct: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string | null;
}

interface Booking {
  id: string;
  bookingCode: string;
  status: string;
  guestName: string;
  checkIn: string;
  checkOut: string;
  rooms: number;
  amount: string;
  client: { id: string; name: string };
}

interface Rate {
  id: string;
  hotelRoomType: { id: string; name: string };
  mealPlan: string;
  occupancy: string;
  rate: string;
  currency: string;
  validFrom: string;
  validTo: string;
  client: { id: string; name: string };
}

interface RoomType {
  id: string;
  name: string;
  code: string | null;
  description: string | null;
  maxOccupancy: number | null;
  sortOrder: number;
  status: 'active' | 'archived';
}

interface AuditEntry {
  id: string;
  action: string;
  at: string;
  user: { id: string; fullName: string; email: string } | null;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const RETAINER_LABELS: Record<Hotel['retainerStatus'], string> = {
  active: 'Active',
  paused: 'Paused',
  terminated: 'Terminated',
};

const BOOKING_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  tentative: 'Tentative',
  confirmed: 'Confirmed',
  voucher_issued: 'Voucher Issued',
  paid: 'Paid',
  completed: 'Completed',
  released: 'Released',
  cancelled: 'Cancelled',
};

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'room-types', label: 'Room types' },
  { id: 'bookings', label: 'Bookings' },
  { id: 'rates', label: 'Rates' },
  { id: 'audit', label: 'Audit log' },
] as const;

type TabId = (typeof TABS)[number]['id'];

// ── Sub-components ────────────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div className="flex gap-4 py-2.5 border-b border-neutral-100 last:border-0">
      <span className="w-40 shrink-0 text-sm text-neutral-500">{label}</span>
      <span className="text-sm text-neutral-900">{value ?? '—'}</span>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white">
      <div className="px-4 py-3 border-b border-neutral-100">
        <h3 className="text-sm font-semibold text-neutral-700">{title}</h3>
      </div>
      <div className="px-4 py-1">{children}</div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-dashed border-neutral-300 py-12 text-center text-sm text-neutral-500">
      {message}
    </div>
  );
}

function ErrorState({ error }: { error: unknown }) {
  const msg =
    error instanceof ApiError && error.status === 403
      ? "You don't have permission to view this data."
      : 'Failed to load data.';
  return (
    <div className="rounded-lg bg-danger-50 px-4 py-3 text-sm text-danger-700">{msg}</div>
  );
}

// ── Room types tab ────────────────────────────────────────────────────────────

const roomTypeSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  code: z.string().max(20).optional(),
  description: z.string().optional(),
  maxOccupancy: z.coerce.number().int().min(1).optional().or(z.literal('')),
  sortOrder: z.coerce.number().int().min(0).optional().or(z.literal('')),
});
type RoomTypeFormValues = z.infer<typeof roomTypeSchema>;

function RoomTypeDialog({
  hotelId,
  editing,
  onClose,
}: {
  hotelId: string;
  editing: RoomType | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<RoomTypeFormValues>({
    resolver: zodResolver(roomTypeSchema),
    defaultValues: editing
      ? {
          name: editing.name,
          code: editing.code ?? undefined,
          description: editing.description ?? undefined,
          maxOccupancy: editing.maxOccupancy ?? undefined,
          sortOrder: editing.sortOrder,
        }
      : { sortOrder: 0 },
  });

  const mutation = useMutation({
    mutationFn: (vals: RoomTypeFormValues) => {
      const body = {
        ...vals,
        maxOccupancy: vals.maxOccupancy === '' ? undefined : vals.maxOccupancy,
        sortOrder: vals.sortOrder === '' ? 0 : vals.sortOrder,
      };
      return editing
        ? api.patch(`/hotels/${hotelId}/room-types/${editing.id}`, body)
        : api.post(`/hotels/${hotelId}/room-types`, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['hotels', hotelId, 'room-types'] });
      toast.success(editing ? 'Room type updated' : 'Room type added');
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Failed to save room type';
      setError('root', { message: msg });
    },
  });

  return (
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
      <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-lg shadow-xl z-50 p-6">
        <Dialog.Title className="text-base font-semibold text-neutral-900 mb-4">
          {editing ? 'Edit room type' : 'Add room type'}
        </Dialog.Title>
        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="rt-name">Name *</Label>
            <Input id="rt-name" {...register('name')} placeholder="Deluxe, Suite, Standard…" />
            {errors.name && <p className="text-xs text-danger-700">{errors.name.message}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="rt-code">Code</Label>
              <Input id="rt-code" {...register('code')} placeholder="DLX" />
              {errors.code && <p className="text-xs text-danger-700">{errors.code.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rt-max-occ">Max occupancy</Label>
              <Input id="rt-max-occ" type="number" min={1} {...register('maxOccupancy')} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rt-desc">Description</Label>
            <Input id="rt-desc" {...register('description')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rt-sort">Sort order</Label>
            <Input id="rt-sort" type="number" min={0} {...register('sortOrder')} />
          </div>
          {errors.root && (
            <p className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-700">
              {errors.root.message}
            </p>
          )}
          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : editing ? 'Save changes' : 'Add room type'}
            </Button>
            <Dialog.Close asChild>
              <Button type="button" variant="outline">Cancel</Button>
            </Dialog.Close>
          </div>
        </form>
      </Dialog.Content>
    </Dialog.Portal>
  );
}

function RoomTypesTab({ hotelId }: { hotelId: string }) {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<RoomType | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['hotels', hotelId, 'room-types'],
    queryFn: () => api.get<RoomType[]>(`/hotels/${hotelId}/room-types`),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/hotels/${hotelId}/room-types/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['hotels', hotelId, 'room-types'] });
      toast.success('Room type archived');
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 422) {
        toast.error(err.message);
      } else {
        toast.error('Failed to archive room type');
      }
    },
  });

  const openAdd = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (rt: RoomType) => { setEditing(rt); setDialogOpen(true); };
  const closeDialog = () => setDialogOpen(false);

  if (isLoading) return <div className="flex justify-center py-10"><Spinner className="h-5 w-5" /></div>;
  if (error) return <ErrorState error={error} />;

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={openAdd}>Add room type</Button>
      </div>

      {!data?.length ? (
        <EmptyState message="No room types defined for this hotel yet." />
      ) : (
        <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50">
                <th className="px-4 py-3 text-left font-medium text-neutral-500">Name</th>
                <th className="px-4 py-3 text-left font-medium text-neutral-500">Code</th>
                <th className="px-4 py-3 text-left font-medium text-neutral-500">Max occ.</th>
                <th className="px-4 py-3 text-left font-medium text-neutral-500">Sort</th>
                <th className="px-4 py-3 text-right font-medium text-neutral-500"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {data.map((rt) => (
                <tr key={rt.id} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-neutral-900">{rt.name}</td>
                  <td className="px-4 py-3 text-neutral-500 font-mono text-xs">{rt.code ?? '—'}</td>
                  <td className="px-4 py-3 text-neutral-600">{rt.maxOccupancy ?? '—'}</td>
                  <td className="px-4 py-3 text-neutral-500">{rt.sortOrder}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        onClick={() => openEdit(rt)}
                        className="text-xs text-neutral-400 hover:text-tangerine-600"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Archive "${rt.name}"? This cannot be undone if rates use it.`)) {
                            archiveMutation.mutate(rt.id);
                          }
                        }}
                        className="text-xs text-neutral-400 hover:text-danger-600"
                      >
                        Archive
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog.Root open={dialogOpen} onOpenChange={(v) => !v && closeDialog()}>
        {dialogOpen && (
          <RoomTypeDialog hotelId={hotelId} editing={editing} onClose={closeDialog} />
        )}
      </Dialog.Root>
    </div>
  );
}

// ── Other tabs ────────────────────────────────────────────────────────────────

function OverviewTab({ hotel }: { hotel: Hotel }) {
  return (
    <div className="space-y-4">
      <SectionCard title="Location">
        <InfoRow label="City" value={hotel.city} />
        <InfoRow label="State" value={hotel.state} />
        <InfoRow label="Country" value={hotel.country} />
        <InfoRow label="Address" value={hotel.address} />
      </SectionCard>

      <SectionCard title="Contact details">
        <InfoRow label="Contact person" value={hotel.contactPerson} />
        <InfoRow label="Email" value={hotel.contactEmail} />
        <InfoRow label="Phone" value={hotel.contactPhone} />
      </SectionCard>

      <SectionCard title="Retainer & commission">
        <InfoRow label="Retainer status" value={RETAINER_LABELS[hotel.retainerStatus]} />
        <InfoRow
          label="Retainer amount"
          value={hotel.retainerAmount ? formatCurrency(hotel.retainerAmount) : null}
        />
        <InfoRow
          label="Default commission"
          value={hotel.defaultCommissionPct ? `${hotel.defaultCommissionPct}%` : null}
        />
      </SectionCard>

      {hotel.notes && (
        <SectionCard title="Notes">
          <p className="py-3 text-sm text-neutral-700 whitespace-pre-wrap">{hotel.notes}</p>
        </SectionCard>
      )}

      <p className="text-xs text-neutral-400">
        Created {formatDate(hotel.createdAt)}
        {hotel.updatedAt && ` · Updated ${formatDate(hotel.updatedAt)}`}
      </p>
    </div>
  );
}

function BookingsTab({ hotelId }: { hotelId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['hotels', hotelId, 'bookings'],
    queryFn: () => api.get<Booking[]>(`/hotels/${hotelId}/bookings`),
  });

  if (isLoading) return <div className="flex justify-center py-10"><Spinner className="h-5 w-5" /></div>;
  if (error) return <ErrorState error={error} />;
  if (!data?.length) return <EmptyState message="No bookings for this hotel yet." />;

  return (
    <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200 bg-neutral-50">
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Booking</th>
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Client</th>
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Guest</th>
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Dates</th>
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Status</th>
            <th className="px-4 py-3 text-right font-medium text-neutral-500">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {data.map((b) => (
            <tr key={b.id} className="hover:bg-neutral-50 transition-colors">
              <td className="px-4 py-3">
                <Link to={`/bookings/${b.id}`} className="font-medium text-neutral-900 hover:text-tangerine-600">
                  {b.bookingCode}
                </Link>
              </td>
              <td className="px-4 py-3 text-neutral-700">{b.client.name}</td>
              <td className="px-4 py-3 text-neutral-600">{b.guestName}</td>
              <td className="px-4 py-3 text-neutral-600 text-xs">
                {formatDateOnly(b.checkIn)} → {formatDateOnly(b.checkOut)}
                <div className="text-neutral-400">{b.rooms} room{b.rooms !== 1 ? 's' : ''}</div>
              </td>
              <td className="px-4 py-3">
                <Badge variant={b.status as Parameters<typeof Badge>[0]['variant']}>
                  {BOOKING_STATUS_LABELS[b.status] ?? b.status}
                </Badge>
              </td>
              <td className="px-4 py-3 text-right font-medium text-neutral-900">
                {formatCurrency(b.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RatesTab({ hotelId }: { hotelId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['hotels', hotelId, 'rates'],
    queryFn: () => api.get<Rate[]>(`/hotels/${hotelId}/rates`),
  });

  if (isLoading) return <div className="flex justify-center py-10"><Spinner className="h-5 w-5" /></div>;
  if (error) return <ErrorState error={error} />;
  if (!data?.length) return <EmptyState message="No contracted rates for this hotel yet." />;

  return (
    <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200 bg-neutral-50">
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Client</th>
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Room type</th>
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Meal plan</th>
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Occupancy</th>
            <th className="px-4 py-3 text-right font-medium text-neutral-500">Rate/night</th>
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Valid</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {data.map((r) => (
            <tr key={r.id} className="hover:bg-neutral-50 transition-colors">
              <td className="px-4 py-3">
                <Link to={`/clients/${r.client.id}`} className="font-medium text-neutral-900 hover:text-tangerine-600">
                  {r.client.name}
                </Link>
              </td>
              <td className="px-4 py-3 text-neutral-700">{r.hotelRoomType?.name}</td>
              <td className="px-4 py-3 text-neutral-600">{r.mealPlan.toUpperCase()}</td>
              <td className="px-4 py-3 text-neutral-600 capitalize">{r.occupancy}</td>
              <td className="px-4 py-3 text-right font-medium text-neutral-900">
                {formatCurrency(r.rate)}
              </td>
              <td className="px-4 py-3 text-xs text-neutral-500">
                {formatDateOnly(r.validFrom)} – {formatDateOnly(r.validTo)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ACTION_LABELS: Record<string, string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
};

function AuditTab({ hotelId }: { hotelId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['hotels', hotelId, 'audit'],
    queryFn: () => api.get<AuditEntry[]>(`/hotels/${hotelId}/audit`),
  });

  if (isLoading) return <div className="flex justify-center py-10"><Spinner className="h-5 w-5" /></div>;
  if (error) return <ErrorState error={error} />;
  if (!data?.length) return <EmptyState message="No audit history for this hotel." />;

  return (
    <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200 bg-neutral-50">
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Action</th>
            <th className="px-4 py-3 text-left font-medium text-neutral-500">By</th>
            <th className="px-4 py-3 text-left font-medium text-neutral-500">When</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {data.map((entry) => (
            <tr key={entry.id} className="hover:bg-neutral-50 transition-colors">
              <td className="px-4 py-3">
                <span
                  className={cn(
                    'inline-flex items-center rounded-sm px-2 py-0.5 text-xs font-medium',
                    entry.action === 'create' && 'bg-success-50 text-success-700',
                    entry.action === 'update' && 'bg-info-50 text-info-700',
                    entry.action === 'delete' && 'bg-danger-50 text-danger-700',
                    !['create', 'update', 'delete'].includes(entry.action) && 'bg-neutral-100 text-neutral-700',
                  )}
                >
                  {ACTION_LABELS[entry.action] ?? entry.action}
                </span>
              </td>
              <td className="px-4 py-3 text-neutral-700">
                {entry.user ? (
                  <div>
                    <span>{entry.user.fullName}</span>
                    <div className="text-xs text-neutral-400">{entry.user.email}</div>
                  </div>
                ) : (
                  <span className="text-neutral-400">System</span>
                )}
              </td>
              <td className="px-4 py-3 text-neutral-500 text-xs">{formatDate(entry.at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function HotelDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  const { data: hotel, isLoading } = useQuery({
    queryKey: ['hotels', id],
    queryFn: () => api.get<Hotel>(`/hotels/${id}`),
    enabled: !!id,
  });

  if (isLoading) {
    return <div className="flex justify-center py-16"><Spinner className="h-6 w-6" /></div>;
  }
  if (!hotel) {
    return <p className="p-6 text-danger-700">Hotel not found.</p>;
  }

  const location = [hotel.city, hotel.state, hotel.country].filter(Boolean).join(', ');

  return (
    <div>
      <PageHeader
        title={hotel.name}
        description={location}
        action={
          <Button asChild>
            <Link to={`/hotels/${id}/edit`}>Edit</Link>
          </Button>
        }
      />

      <div className="p-6 max-w-4xl">
        <BackLink to="/hotels" label="Back to hotels" />

        {/* Status badges */}
        <div className="flex items-center gap-2 mb-5">
          <Badge variant={hotel.retainerStatus}>{RETAINER_LABELS[hotel.retainerStatus]}</Badge>
          {hotel.retainerAmount && (
            <span className="text-xs text-neutral-500 bg-neutral-100 rounded-sm px-2 py-0.5">
              {formatCurrency(hotel.retainerAmount)} retainer
            </span>
          )}
          {hotel.defaultCommissionPct && (
            <span className="text-xs text-neutral-500 bg-neutral-100 rounded-sm px-2 py-0.5">
              {hotel.defaultCommissionPct}% commission
            </span>
          )}
        </div>

        {/* Tab nav */}
        <div className="flex border-b border-neutral-200 mb-5">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === tab.id
                  ? 'border-tangerine-500 text-tangerine-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'overview' && <OverviewTab hotel={hotel} />}
        {activeTab === 'room-types' && <RoomTypesTab hotelId={hotel.id} />}
        {activeTab === 'bookings' && <BookingsTab hotelId={hotel.id} />}
        {activeTab === 'rates' && <RatesTab hotelId={hotel.id} />}
        {activeTab === 'audit' && <AuditTab hotelId={hotel.id} />}
      </div>
    </div>
  );
}
